import React, { useState, useRef } from 'react';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import QRCode from 'qrcode';
import * as Sentry from '@sentry/react';

import { QRCodeListItemProps } from '../types';
import DeleteQRCodeConfirmationDialog from './DeleteQRCodeConfirmationDialog';
import QRCodeViewModal from './QRCodeViewModal';
import { black, alabaster, grayLight } from "design-system/colors";
import { useScreenSize } from "globals/hooks/useScreenSize";
import { useSnackbar, useAnalytics } from 'globals/hooks';
import MoovsEllipsisMenu from "components/MoovsEllipsisMenu";
import { EllipsisMenuOption } from "components/MoovsEllipsisMenu/components/EllipsisMenuList";

function QRCodeListItem(props: QRCodeListItemProps) {
  const { qrCode, onRefresh } = props;

  // hooks
  const { isMobileView } = useScreenSize();
  const snackbar = useSnackbar();
  const { track } = useAnalytics();
  const downloadLinkRef = useRef<HTMLAnchorElement>(null);

  // state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // event handlers
  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleOpenViewModal = () => {
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
  };

  const handleDeleteError = (error: Error) => {
    setIsDeleteDialogOpen(false);
    console.error("Error deleting QR code:", error);
    snackbar.error('There was an error deleting your code');
  };

  const handleDownload = () => {
    QRCode.toDataURL(qrCode.fullUrl, { 
      width: 250,
      margin: 1,
      color: { dark: '#000', light: '#fff' }
    })
      .then(dataUrl => {        
        track('qrCodeApp_download');
        const isMobile = !!global.navigator.userAgent && /iPhone|iPad|iPod|Android/i.test(global.navigator.userAgent);
        
        if (isMobile) {
          // For mobile, open in a new tab where user can long-press to save
          window.open(dataUrl, '_blank');
        } else if (downloadLinkRef.current) {
          // Desktop approach - use the ref to trigger download
          downloadLinkRef.current.href = dataUrl;
          downloadLinkRef.current.download = `${qrCode.displayName || 'qrcode'}.png`;
          downloadLinkRef.current.click();
        }
      })
      .catch(err => {
        Sentry.captureException(err);
        snackbar.error('There was an error downloading your code');
      });
  };

  // derived state
  const menuOptions: EllipsisMenuOption[] = [
    {
      text: 'View',
      onClick: handleOpenViewModal,
    },
    {
      text: 'Download',
      onClick: handleDownload,
    },
    {
      text: 'Delete',
      onClick: handleOpenDeleteDialog,
    },
  ];

  const qrCodeIcon = (
    <Box sx={{ 
      width: 50, 
      height: 50, 
      flexShrink: 0, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      bgcolor: grayLight, 
      borderRadius: '50%',
      mr: isMobileView ? 2 : 0
    }}>
      <QrCode2Icon sx={{ fontSize: 30 }} />
    </Box>
  );

  return (
    <>
      <Paper 
        elevation={0} 
        onClick={handleOpenViewModal}
        sx={{ 
          p: 2, 
          bgcolor: alabaster, 
          borderRadius: 1,
          width: '100%',
          overflow: 'hidden',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: grayLight
          }
        }}
      >
        {isMobileView ? (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                {qrCodeIcon}
                <Typography variant="subtitle1" component="div" sx={{ color: black, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {qrCode.displayName}
                </Typography>
              </Box>
              <Box onClick={(e) => e.stopPropagation()}>
                <MoovsEllipsisMenu
                  size="small"
                  options={menuOptions}
                />
              </Box>
            </Box>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" component="div" sx={{ 
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: black
              }}>
                <Tooltip title={qrCode.fullUrl || qrCode.baseUrl} placement="top" arrow>
                  <span>{qrCode.fullUrl || qrCode.baseUrl}</span>
                </Tooltip>
              </Typography>
            </Box>
          </Box>
        ) : (
          /* Desktop view */
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'auto 1fr auto',
            gap: 2, 
            width: '100%',
            alignItems: 'center'
          }}>
            {qrCodeIcon}
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Typography variant="subtitle1" component="div" sx={{ 
                color: black, 
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {qrCode.displayName}
              </Typography>
              <Typography variant="body2" component="div" sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: black
              }}>
                <Tooltip title={qrCode.fullUrl || qrCode.baseUrl} placement="top" arrow>
                  <span>{qrCode.fullUrl || qrCode.baseUrl}</span>
                </Tooltip>
              </Typography>
            </Box>
            <Box sx={{ justifySelf: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
              <MoovsEllipsisMenu
                size="small"
                options={menuOptions}
              />
            </Box>
          </Box>
        )}
      </Paper>

      <DeleteQRCodeConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        qrCode={qrCode}
        onSuccess={onRefresh}
        onError={handleDeleteError}
      />
      
      <QRCodeViewModal
        qrCode={qrCode}
        open={isViewModalOpen}
        onClose={handleCloseViewModal}
        onDownload={handleDownload}
      />

      {/* Hidden download link */}
      <a 
        ref={downloadLinkRef} 
        style={{ display: 'none' }} 
        href="about:blank"
        aria-label="Download QR code"
        download
      >
        Download QR code
      </a>
    </>
  );
}; 

export default QRCodeListItem;